import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@tecma/ui";
import { datadogRum } from "@datadog/browser-rum";

import { setRealDeviceHeight } from "utils/functions/device";
import { Scaffold } from "components/Scaffold";
import { AlertPopupFactory } from "components/AlertPopupFactory";
import { GenericErrorPage } from "components/GenericErrorPage";
import { applicationInitActions } from "./store/sagas/application/init/init.actions";
import { applicationStateSelectors } from "./store/slices/applicationState/selectors";
import { deviceStateActions } from "./store/slices/deviceState/slice";
import { Routes } from "./routes";

// styles
import "./theme/global.scss";

if (process.env.REACT_APP_NAMESPACE === "biz-tecma-prod") {
  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID || "",
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN || "",
    site: "datadoghq.eu",
    service: "spaceconfigurator",
    env: process.env.REACT_APP_NAMESPACE,
    version: process.env.REACT_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });
}

const App: React.FC = () => {
  const dispatch = useDispatch();
  const isInit = useSelector(applicationStateSelectors.selectIsInit);
  const isLoading = useSelector(applicationStateSelectors.selectIsLoading);
  const scaffoldClassList = isInit ? "app-is-loading" : "";
  const hasError = useSelector(applicationStateSelectors.selectHasError);

  useEffect(() => {
    dispatch(applicationInitActions.initApplicationRequested());
  }, []);

  useEffect(() => {
    setRealDeviceHeight();
    function handleResize() {
      dispatch(
        deviceStateActions.setDeviceDimensions({
          height: window.innerHeight,
          width: window.innerWidth,
        }),
      );
      setRealDeviceHeight();
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <BrowserRouter>
      {hasError ? (
        <GenericErrorPage />
      ) : (
        <>
          {isInit ? (
            <Spinner type='dotted' className='page-loader' />
          ) : (
            <Scaffold className={scaffoldClassList}>
              <Routes />
            </Scaffold>
          )}
          {!isInit && !isLoading && <AlertPopupFactory />}
        </>
      )}
    </BrowserRouter>
  );
};

export default App;
